import { createContext, useContext, useMemo, type ReactNode } from 'react'

import { type LayoutConfig, type LayoutShell } from '@hooks/useLayoutVariant'

interface LayoutProviderProps {
  children: ReactNode
  layout: LayoutConfig
}

interface LayoutContextValue {
  layout: LayoutConfig
  shell: LayoutShell
  isFullLayout: boolean
  hasSidebar: boolean
  hasTopbar: boolean
  hasNotices: boolean
}

const fallbackLayout: LayoutConfig = {
  shell: 'minimal-anon',
  menu: 'hidden',
  topbar: 'hidden',
  content: 'container',
  notices: 'hidden',
}

const fallbackValue: LayoutContextValue = {
  layout: fallbackLayout,
  shell: fallbackLayout.shell,
  isFullLayout: false,
  hasSidebar: false,
  hasTopbar: false,
  hasNotices: false,
}

const LayoutContext = createContext<LayoutContextValue>(fallbackValue)

export const LayoutProvider = ({ children, layout }: LayoutProviderProps) => {
  const value = useMemo<LayoutContextValue>(
    () => ({
      layout,
      shell: layout.shell,
      isFullLayout: layout.shell === 'full',
      hasSidebar: layout.menu === 'visible',
      hasTopbar: layout.topbar === 'visible',
      hasNotices: layout.notices === 'visible',
    }),
    [layout]
  )

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
}

export const useLayout = () => useContext(LayoutContext)
